import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { JuanaChatService } from '../../../core/services/juana-chat.service';
import { CreateOrderPayload, PartsFees } from '../../../core/models/orders/order.types';
import { RepuestosOrdersService } from '../../../core/services/repuestos-orders.service';
import { JuanaBuyRequest, JuanaFieldOptionSelection, JuanaScheduleRequest } from '../juana-card-renderer/juana-card-renderer.component';
import { SchedulingService } from '../../../core/services/scheduling.service';
import { VehiclesService } from '../../../core/services/vehicles.services';
import { Vehicle } from '../../../core/models/vehicles/vehicle.model';
import { CreateScheduling } from '../../../core/models/scheduling/create-scheduling.model';

/**
 * Superficie desde la que se monta el panel: solo cambia el subtítulo del
 * header. 'general' es el home de Juana, donde no está acotada a Repuestos.
 * Vive acá (no en RepuestosComponent) porque el panel ya es compartido.
 */
export type AgentVariant = 'catalog' | 'configure' | 'tracking' | 'general';

type DeliveryOptions = 'pickup' | 'delivery' | 'both' | null;

interface FleetVehicle {
  vehicleId?: string;
  id?: string;
  brand: string;
  model: string;
  year: string | number;
  plate?: string;
}

interface FleetVehiclesCard {
  kind: 'fleet-vehicles';
  vehicles: FleetVehicle[];
}

/** Mini-formulario de confirmación de compra de una oferta concreta. */
interface BuyForm {
  responseId: string;
  requestId?: string;
  providerName: string;
  unitPrice: number;
  deliveryOptions: DeliveryOptions;
  deliveryFee: number | null;
  qty: number;
  deliveryMode: 'pickup' | 'delivery';
  address: string;
  submitting: boolean;
}

/**
 * Mini-formulario de confirmación de cita: aliado/servicio/precio ya vienen
 * fijados por la tarjeta de `search_services`; el usuario solo elige
 * vehículo y fecha/hora. Al confirmar llama el endpoint de agendar
 * DIRECTO (SchedulingService), igual que `confirmBuy()` — el LLM nunca
 * dispara el cobro.
 */
interface ScheduleForm {
  providerId: string;
  providerName: string;
  serviceId: string;
  serviceName: string;
  subServiceId: string;
  subServiceName: string;
  price: number | null;
  vehicleId: string;
  datetime: string;
  submitting: boolean;
}

@Component({
  selector: 'app-agent-panel',
  standalone: false,
  templateUrl: './agent-panel.component.html',
  styleUrl: './agent-panel.component.scss',
})
export class AgentPanelComponent implements OnInit {
  @Input() variant: AgentVariant = 'catalog';
  /**
   * false = solo la lista de mensajes, sin header ni composer propios. Lo usa
   * el home, que ya tiene los suyos y monta el chat DENTRO de su lienzo oscuro
   * en vez de reemplazarlo por esta superficie (el chat no debe "saltar" a otra
   * interfaz al iniciar la conversación).
   */
  @Input() chrome = true;
  input = '';
  /** Mini-form de compra abierto para una oferta (solo uno a la vez). */
  buyForm: BuyForm | null = null;
  /** Mini-form de agendar abierto para una fila de `booking-services` (solo uno a la vez). */
  scheduleForm: ScheduleForm | null = null;
  /** Tarifas vigentes (comisión corp + costo de envío) para mostrar el total. */
  fees: PartsFees | null = null;
  /** Vehículos de la flota, para el selector del mini-form de agendar. */
  vehicles: Vehicle[] = [];

  constructor(
    public juana: JuanaChatService,
    private orders: RepuestosOrdersService,
    private scheduling: SchedulingService,
    private vehiclesService: VehiclesService,
    private router: Router,
    private transloco: TranslocoService,
  ) {}

  /** Abre la tabla "Mis solicitudes" (ofertas persistentes, fuera del chat). */
  verSolicitudes(): void {
    void this.router.navigate(['/dashboard/repuestos/solicitudes']);
  }

  /** Deep-link al detalle de una solicitud específica desde una tarjeta del chat. */
  verSolicitud(requestId?: string): void {
    if (!requestId) {
      this.verSolicitudes();
      return;
    }
    void this.router.navigate(['/dashboard/repuestos/solicitudes', requestId]);
  }

  ngOnInit(): void {
    // Crea la conversación y conecta el socket (idempotente).
    void this.juana.init();
    // Tarifas vigentes para mostrar el total exacto en el mini-form de compra.
    this.orders.getPartsFees().subscribe({
      next: f => (this.fees = f),
      error: () => (this.fees = null),
    });
    // Flota del corporativo, para el selector del mini-form de agendar.
    this.vehiclesService.getListVehiclesAssigned().subscribe({
      next: v => (this.vehicles = v),
      error: () => (this.vehicles = []),
    });
  }

  get subtitle(): string {
    switch (this.variant) {
      case 'configure':
        return this.transloco.translate('repuestos.agent.subtitleConfigure');
      case 'tracking':
        return this.transloco.translate('repuestos.agent.subtitleTracking');
      case 'general':
        return this.transloco.translate('repuestos.agent.subtitleGeneral');
      default:
        return this.transloco.translate('repuestos.agent.subtitleDefault');
    }
  }

  get placeholder(): string {
    return this.transloco.translate('repuestos.agent.inputPlaceholder');
  }

  /** Sugerencias rápidas que envían un mensaje al tocarlas. */
  get quickPrompts(): string[] {
    return [
      this.transloco.translate('repuestos.agent.quickPrompt1'),
      this.transloco.translate('repuestos.agent.quickPrompt2'),
      this.transloco.translate('repuestos.agent.quickPrompt3'),
    ];
  }

  /** Botón "Otro vehículo": manda a Juana que es para un vehículo fuera de la flota. */
  sendOtherVehicle(): void {
    this.send(this.transloco.translate('repuestos.agent.otherVehicleMsg'));
  }

  send(text?: string): void {
    const value = (text ?? this.input).trim();
    if (!value) return;
    this.input = '';
    void this.juana.send(value);
  }

  /** Descarta la conversación actual (con confirmación) e inicia una nueva. */
  nuevaConversacion(): void {
    if (
      this.juana.messages().length > 0 &&
      !window.confirm(this.transloco.translate('repuestos.agent.newConvConfirm'))
    ) {
      return;
    }
    this.input = '';
    this.buyForm = null;
    this.scheduleForm = null;
    void this.juana.newConversation();
  }

  /**
   * El usuario eligió un vehículo de su flota (card `fleet-vehicles`): se lo
   * decimos a Juana en lenguaje natural; el LLM lo mapea al vehicleId real
   * (que ya tiene del resultado de list_fleet_vehicles).
   */
  elegirVehiculo(v: {
    brand?: string;
    model?: string;
    year?: string | number;
    plate?: string;
  }): void {
    const desc = [v.brand, v.model, v.year].filter(Boolean).join(' ').trim();
    const msg =
      this.transloco.translate('repuestos.agent.forMyVehicle', {
        desc: desc || this.transloco.translate('repuestos.agent.vehicleFallback'),
      }) + (v.plate ? this.transloco.translate('repuestos.agent.plateSuffix', { plate: v.plate }) : '');
    this.send(msg);
  }

  /**
   * El usuario eligió una opción de la tarjeta `field-options` (p. ej. tipo
   * de vehículo o estado): mismo patrón que `elegirVehiculo`, se reenvía en
   * lenguaje natural para que el LLM la mapee al campo real.
   */
  onFieldOptionSelected(evt: JuanaFieldOptionSelection): void {
    const msg = this.transloco.translate('repuestos.agent.fieldOptionAnswer', {
      label: evt.label.toLowerCase(),
    });
    this.send(msg);
  }

  // Tipos de cards que se renderizan DENTRO del bubble del asistente
  private readonly INLINE_CARD_KINDS = ['fleet-vehicles'];

  /** Devuelve las cards del mensaje ANTERIOR si deben ir inline en este bubble de asistente. */
  getInlineCard(assistantIndex: number): FleetVehiclesCard | null {
    const prev = this.juana.messages()[assistantIndex - 1];
    if (prev?.role === 'tool' && prev.cards && this.INLINE_CARD_KINDS.includes(prev.cards.kind)) {
      return prev.cards as unknown as FleetVehiclesCard;
    }
    return null;
  }

  /** Devuelve true si esta card de tool será renderizada inline en el bubble siguiente. */
  isInlinedCard(toolIndex: number): boolean {
    const next = this.juana.messages()[toolIndex + 1];
    const current = this.juana.messages()[toolIndex];
    return !!(next?.role === 'assistant' && current?.cards && this.INLINE_CARD_KINDS.includes(current.cards.kind));
  }

  /**
   * CTA "Comprar con este aliado": abre el mini-form de confirmación
   * (cantidad + tipo de entrega) para crear la orden desde el responseId
   * de la oferta. Antes esto mandaba texto libre a Juana, que re-buscaba el
   * producto en el inventario del aliado y respondía "no lo tiene".
   */
  /** CTA de compra emitido por una tarjeta del card-renderer. */
  onBuyRequest(req: JuanaBuyRequest): void {
    this.openBuyForm(
      req.responseId,
      req.providerName,
      req.requestId,
      req.price ?? undefined,
      req.deliveryOptions ?? null,
      req.deliveryFee ?? null,
    );
  }

  openBuyForm(
    responseId: string,
    providerName: string,
    requestId?: string,
    unitPrice?: number,
    deliveryOptions?: DeliveryOptions,
    deliveryFee?: number | null,
  ): void {
    if (!responseId) return;
    const opts: DeliveryOptions = deliveryOptions ?? null;
    // Default: si el aliado solo hace domicilio, arranca en domicilio; si no, recoger.
    const deliveryMode: 'pickup' | 'delivery' = opts === 'delivery' ? 'delivery' : 'pickup';
    this.buyForm = {
      responseId,
      requestId,
      providerName,
      unitPrice: Number(unitPrice) || 0,
      deliveryOptions: opts,
      deliveryFee: deliveryFee == null ? null : Number(deliveryFee),
      qty: 1,
      deliveryMode,
      address: '',
      submitting: false,
    };
  }

  cancelBuy(): void {
    this.buyForm = null;
  }

  // ---- Opciones de entrega permitidas según lo que ofrece el aliado ----
  // Si la oferta no especifica (null/undefined, datos viejos), se permiten AMBAS.
  canPickup(f: BuyForm): boolean {
    return f.deliveryOptions !== 'delivery'; // pickup, both, o sin dato → true
  }
  canDelivery(f: BuyForm): boolean {
    return f.deliveryOptions !== 'pickup'; // delivery, both, o sin dato → true
  }

  // ---- Desglose de precio en vivo (calca el cálculo del backend) ----
  buyQty(f: BuyForm): number {
    return Math.max(1, Math.floor(Number(f.qty) || 1));
  }
  buySubtotal(f: BuyForm): number {
    return f.unitPrice * this.buyQty(f);
  }
  buyServiceFee(f: BuyForm): number {
    const rate = this.fees?.customerFeeRate ?? 0;
    return Math.round(this.buySubtotal(f) * rate);
  }
  /** Envío que cobra el aliado en esta oferta; si no lo trae, el default. */
  offerDeliveryFee(f: BuyForm): number {
    return f.deliveryFee != null ? f.deliveryFee : this.fees?.deliveryFeeCop ?? 0;
  }
  buyDeliveryFee(f: BuyForm): number {
    if (f.deliveryMode !== 'delivery') return 0;
    return this.offerDeliveryFee(f);
  }
  buyPreStripeFee(f: BuyForm): number {
    return this.buySubtotal(f) + this.buyServiceFee(f) + this.buyDeliveryFee(f);
  }
  buyStripeFee(f: BuyForm): number {
    return Math.round(this.buyPreStripeFee(f) * 0.029) + 900;
  }
  buyTotal(f: BuyForm): number {
    return this.buyPreStripeFee(f) + this.buyStripeFee(f);
  }

  /** Crea la orden de forma determinista desde el responseId de la oferta. */
  confirmBuy(): void {
    const f = this.buyForm;
    if (!f || f.submitting) return;
    if (f.deliveryMode === 'delivery' && !f.address.trim()) {
      this.juana.pushAssistantNotice(this.transloco.translate('repuestos.agent.enterAddress'));
      return;
    }
    const qty = Math.max(1, Math.floor(Number(f.qty) || 1));
    f.submitting = true;
    const payload: CreateOrderPayload = {
      items: [{ responseId: f.responseId, qty }],
      deliveryMode: f.deliveryMode,
      address: f.deliveryMode === 'delivery' ? f.address : undefined,
      requestId: f.requestId,
      conversationId: this.juana.conversationIdValue ?? undefined,
    };
    this.orders.createOrder(payload).subscribe({
      next: ({ order, paymentUrl }) => {
        this.juana.pushOrderCard({
          kind: 'order-payment-link',
          orderId: order.id,
          total: order.pricing.total,
          deliveryMode: order.deliveryMode,
          paymentUrl,
        });
        this.buyForm = null;
      },
      error: err => {
        const msg =
          err?.error?.error?.message ||
          err?.error?.message ||
          this.transloco.translate('repuestos.agent.couldNotCreateOrder');
        this.juana.pushAssistantNotice(`⚠️ ${msg}`);
        if (this.buyForm) this.buyForm.submitting = false;
      },
    });
  }

  /** CTA "Agendar con este aliado" de una fila de `booking-services`. */
  onScheduleRequest(req: JuanaScheduleRequest): void {
    this.scheduleForm = {
      providerId: req.providerId,
      providerName: req.providerName,
      serviceId: req.serviceId,
      serviceName: req.serviceName,
      subServiceId: req.subServiceId,
      subServiceName: req.subServiceName,
      price: req.price,
      vehicleId: '',
      datetime: '',
      submitting: false,
    };
  }

  cancelSchedule(): void {
    this.scheduleForm = null;
  }

  get scheduleRowKey(): string | null {
    const f = this.scheduleForm;
    return f ? `${f.providerId}:${f.subServiceId}` : null;
  }

  /**
   * Mínimo para el input datetime-local: "ahora" (el backend exige al menos
   * 5 minutos en el futuro; el margen real lo valida el servidor, esto solo
   * evita elegir una hora obviamente pasada).
   */
  get scheduleMinDatetime(): string {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 10);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /** Crea la cita de forma determinista, con el mismo endpoint que usa Agendamiento. */
  confirmSchedule(): void {
    const f = this.scheduleForm;
    if (!f || f.submitting) return;
    if (!f.vehicleId) {
      this.juana.pushAssistantNotice(this.transloco.translate('repuestos.agent.noVehicleSelected'));
      return;
    }
    if (!f.datetime) {
      this.juana.pushAssistantNotice(this.transloco.translate('repuestos.agent.noDateSelected'));
      return;
    }
    f.submitting = true;
    const payload: CreateScheduling = {
      userProviderId: f.providerId,
      vehicleId: f.vehicleId,
      serviceId: f.serviceId,
      subServices: [{ subServiceId: f.subServiceId }],
      datetime: this.toLocalIso(new Date(f.datetime)),
    };
    this.scheduling.createScheduling(payload).subscribe({
      next: ({ booking }) => {
        this.juana.pushOrderCard({
          kind: 'booking-confirmed',
          booking: {
            serviceName: booking.serviceName,
            subServiceName: booking.subServiceName,
            providerName: f.providerName,
            plate: booking.vehicle?.plate,
            date: booking.date,
            hour: booking.hour,
            totalAmount: booking.transaction?.totalAmount ?? null,
          },
        });
        this.scheduleForm = null;
      },
      error: err => {
        const msg =
          err?.error?.error?.message ||
          err?.error?.message ||
          this.transloco.translate('repuestos.agent.couldNotSchedule');
        this.juana.pushAssistantNotice(`⚠️ ${msg}`);
        if (this.scheduleForm) this.scheduleForm.submitting = false;
      },
    });
  }

  /** Misma conversión que scheduling-form.component.ts (offset fijo de Colombia). */
  private toLocalIso(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}-05:00`;
  }

  /**
   * Renderiza markdown ligero (negritas, cursivas, viñetas, saltos de línea)
   * de Juana a HTML. Escapa el HTML crudo PRIMERO, así solo se inyectan las
   * etiquetas que generamos nosotros (a prueba de XSS); Angular además
   * sanitiza el [innerHTML].
   */
  renderRich(text: string): string {
    const escaped = (text ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    let html = escaped
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/(?<!\*)\*(?!\s)([^*]+?)\*(?!\*)/g, '<em>$1</em>');

    const lines = html.split('\n');
    let out = '';
    let inList = false;
    for (const line of lines) {
      const bullet = line.match(/^\s*[-•]\s+(.*)$/);
      if (bullet) {
        if (!inList) {
          out += '<ul>';
          inList = true;
        }
        out += '<li>' + bullet[1] + '</li>';
      } else {
        if (inList) {
          out += '</ul>';
          inList = false;
        }
        out += line + '<br>';
      }
    }
    if (inList) out += '</ul>';
    return out.replace(/(<br>)+$/, '');
  }
}
